import { message, Alert } from "antd";
import ScreeningForm from "./components/ScreeningForm";
import "./index.scss";
import {
  submitPublicScreening,
  PublicScreeningResponse,
  notifyOnRateLimitReset,
} from "../../Service/public.screening.service";
import { useRef, useState } from "react";
import ScreeningReport, { StructuredScreeningReport } from "./components/ScreeningReport";
import RateLimitModal from "./components/RateLimitModal";
import ProcessingScreen from "./components/ProcessingScreen";
import { useProfileScreening } from "../../Context/ProfileScreeningContext";
import { useNavigate } from "react-router-dom";
import { classifyError } from "../../Utils/errorClassifier";
import { ERROR_MESSAGES } from "../../Utils/errorMessages";
import { ErrorCategory } from "../../Utils/errorClassifier";

// ─── Dev dummy data ──────────────────────────────────────────────────────────
// Set REACT_APP_SHOW_DUMMY_REPORT=true in .env.local to bypass the form and
// immediately render the report for rapid UI development.

const SHOW_DUMMY_REPORT = process.env.REACT_APP_SHOW_DUMMY_REPORT === "true";

const DUMMY_RESULT: PublicScreeningResponse = {
  session_id: "dummy-session-001",
  report: {
    candidate: {
      name: "Arjun Mehta",
      role: "MERN Stack Developer",
      experience_years: 0.17,
      primary_skills: ["Node.js", "JavaScript", "MongoDB", "RESTful APIs", "Responsive Design"],
    },
    fit_overview: {
      fit_score: 45,
      fit_category: "Partial Match",
      confidence: "Medium",
      recommendation: "Proceed with Caution · Next Step: Skill Assessment",
      summary:
        "Arjun Mehta is a motivated Full Stack Developer fresher with foundational skills in JavaScript, Node.js, and MongoDB. While he demonstrates potential through projects and internships, he lacks direct experience with the complete MERN stack and several must-have requirements such as Express.js, React.js, and advanced web development practices.",
    },
    job_requirement_coverage: [
      { requirement_id: "1", requirement: "Develop, test, and deploy full-stack applications using the MERN stack.", priority: "Must-have", evidence: "Experience with Node.js and MongoDB in projects.", coverage: "Medium" },
      { requirement_id: "2", requirement: "Design RESTful APIs and integrate third-party services.", priority: "Must-have", evidence: "Implemented RESTful APIs in Task Manager Application.", coverage: "Strong" },
      { requirement_id: "3", requirement: "Collaborate with product managers, designers, and other developers to deliver high-quality features.", priority: "Must-have", evidence: "Collaborated with senior developers during internship.", coverage: "Medium" },
      { requirement_id: "4", requirement: "Optimize applications for maximum speed, scalability, and security.", priority: "Must-have", evidence: "No direct evidence of optimization experience.", coverage: "Missing" },
      { requirement_id: "5", requirement: "Maintain clean, reusable, and well-documented code.", priority: "Must-have", evidence: "No direct evidence of code maintenance practices.", coverage: "Missing" },
      { requirement_id: "6", requirement: "Troubleshoot, debug, and upgrade existing applications.", priority: "Must-have", evidence: "No direct evidence of troubleshooting or debugging experience.", coverage: "Missing" },
      { requirement_id: "7", requirement: "Stay updated with emerging technologies and industry trends.", priority: "Must-have", evidence: "Passionate about learning modern technologies.", coverage: "Medium" },
      { requirement_id: "8", requirement: "Strong proficiency in JavaScript (ES6+) and modern frameworks.", priority: "Must-have", evidence: "Proficient in JavaScript.", coverage: "Medium" },
      { requirement_id: "9", requirement: "Hands-on experience with MongoDB, Express.js, React.js, Node.js.", priority: "Must-have", evidence: "Experience with MongoDB and Node.js.", coverage: "Medium" },
      { requirement_id: "10", requirement: "Familiarity with Redux, REST APIs, GraphQL.", priority: "Must-have", evidence: "Familiar with REST APIs.", coverage: "Medium" },
      { requirement_id: "11", requirement: "Experience with version control (Git/GitHub) and CI/CD pipelines.", priority: "Must-have", evidence: "Experience with Git/GitHub.", coverage: "Medium" },
      { requirement_id: "12", requirement: "Knowledge of cloud platforms (AWS, Azure, or GCP) is a plus.", priority: "Nice-to-have", evidence: "No evidence of cloud platform knowledge.", coverage: "Missing" },
      { requirement_id: "13", requirement: "Understanding of UI/UX principles and responsive design.", priority: "Must-have", evidence: "Developed responsive web applications.", coverage: "Medium" },
      { requirement_id: "14", requirement: "Bachelor's degree in Computer Science, Engineering, or related field.", priority: "Must-have", evidence: "Pursuing B.Tech in Computer Science.", coverage: "Strong" },
      { requirement_id: "15", requirement: "Experience with TypeScript and Next.js.", priority: "Nice-to-have", evidence: "No evidence of TypeScript or Next.js experience.", coverage: "Missing" },
      { requirement_id: "16", requirement: "Exposure to Docker/Kubernetes for containerized deployments.", priority: "Nice-to-have", evidence: "No evidence of Docker/Kubernetes exposure.", coverage: "Missing" },
      { requirement_id: "17", requirement: "Prior work in SaaS or HR Tech platforms.", priority: "Nice-to-have", evidence: "No evidence of SaaS or HR Tech experience.", coverage: "Missing" },
      { requirement_id: "18", requirement: "Strong problem-solving and communication skills.", priority: "Nice-to-have", evidence: "Demonstrated problem-solving skills in projects.", coverage: "Medium" },
    ],
    skill_match_breakdown: {
      strong: ["Node.js", "JavaScript", "MongoDB", "RESTful APIs"],
      partial: ["Responsive Design", "Git/GitHub", "Problem-solving"],
      missing: ["React.js", "TypeScript", "Docker/Kubernetes", "Cloud Platforms", "Code Maintenance", "SaaS/HR Tech", "Next.js", "Troubleshooting", "Express.js", "Optimization"],
    },
    validation_gaps: [
      { skill: "Express.js", reason: "No evidence of experience with Express.js." },
      { skill: "React.js", reason: "No evidence of experience with React.js." },
      { skill: "Optimization", reason: "No evidence of experience in optimizing applications." },
      { skill: "Code Maintenance", reason: "No evidence of maintaining clean, reusable, and well-documented code." },
      { skill: "Troubleshooting", reason: "No evidence of troubleshooting or debugging experience." },
    ],
    recommended_assessments: [
      { skill_area: "Express.js", reason_to_validate: "No evidence of experience with Express.js.", assessment_type: "Coding" },
      { skill_area: "React.js", reason_to_validate: "No evidence of experience with React.js.", assessment_type: "Coding" },
      { skill_area: "Optimization", reason_to_validate: "No evidence of experience in optimizing applications.", assessment_type: "Scenario-Based" },
      { skill_area: "Code Maintenance", reason_to_validate: "No evidence of maintaining clean, reusable, and well-documented code.", assessment_type: "Descriptive" },
      { skill_area: "Troubleshooting", reason_to_validate: "No evidence of troubleshooting or debugging experience.", assessment_type: "Scenario-Based" },
    ],
  } as StructuredScreeningReport,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ScreeningStatus = "idle" | "processing" | "success" | "error";

interface ScreeningErrorState {
  statusCode?: number;
  message?: string;
  retryAfter?: number;
  category?: ErrorCategory;
  headline?: string;
  body?: string;
  guidance?: string[];
  isRetryable?: boolean;
  requiresUserAction?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

const ProfileScreeningPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ScreeningStatus>("idle");
  const [result, setResult] = useState<PublicScreeningResponse | null>(null);
  const [error, setError] = useState<ScreeningErrorState | null>(null);
  const [prefillJobDescription, setPrefillJobDescription] = useState<string>("");

  // Track the submitted job description so we can store it in sessionStorage when a sign-up CTA is clicked
  const submittedJobDescriptionRef = useRef<string>("");

  const {
    retryAfter,
    countdownSeconds,
    limitType: contextLimitType,
    isRateLimited,
    rateLimitModalOpen,
    setRateLimitState,
    openRateLimitModal,
    closeRateLimitModal,
    setScreeningSessionId,
    setSubmittedJobDescription,
  } = useProfileScreening();

  // Effective values — override with dummy data in dev mode
  const effectiveStatus: ScreeningStatus = SHOW_DUMMY_REPORT ? "success" : status;
  const effectiveResult = SHOW_DUMMY_REPORT ? DUMMY_RESULT : result;

  const isProcessing = status === "processing";

  // Format countdown seconds to HH:MM:SS or MM:SS
  const formatCountdown = (seconds: number | undefined): string => {
    if (!seconds || seconds <= 0) return '';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleRateLimitNotify = async (email: string) => {
    await notifyOnRateLimitReset(email);
  };

  const handleScreeningSubmit = async (params: {
    jobDescription: string;
    resumeFile: File;
    captchaToken: string;
  }) => {
    // Store job description for use in sign-up CTA handlers
    submittedJobDescriptionRef.current = params.jobDescription;

    setStatus("processing");
    setError(null);
    setResult(null);

    try {
      const data = await submitPublicScreening({
        jobDescription: params.jobDescription,
        resumeFile: params.resumeFile,
        captchaToken: params.captchaToken,
      });

      setResult(data);
      setStatus("success");
      // Expose session ID and job description in context so the Navigator Sign Up
      // button can include them even without the user clicking a report CTA
      setScreeningSessionId(data.session_id);
      setSubmittedJobDescription(params.jobDescription);
    } catch (err: any) {
      const classified = classifyError(err);
      const msgs = ERROR_MESSAGES[classified.category];
      const statusCode = err?.response?.status as number | undefined;
      const data = err?.response?.data;

      // Special handling for rate limit — show modal
      if (classified.category === 'rate_limited') {
        const after = data?.retry_after;
        const lt = data?.limit_type as 'hourly' | 'daily' | undefined;
        setError({
          statusCode,
          message: msgs.candidateHeadline,
          category: classified.category,
          headline: msgs.candidateHeadline,
          body: msgs.candidateBody,
          retryAfter: after,
        });
        setRateLimitState(true, typeof after === "number" ? after : undefined, lt);
        openRateLimitModal();
      } else {
        // All other errors: use classified error
        setError({
          statusCode,
          category: classified.category,
          headline: msgs.candidateHeadline,
          body: msgs.candidateBody,
          guidance: msgs.candidateGuidance,
          isRetryable: classified.isRetryable,
          requiresUserAction: classified.requiresUserAction,
        });

        // Only show toast for transient errors (not resume-specific)
        if (['ai_unavailable', 'network_failure'].includes(classified.category)) {
          message.error(msgs.candidateHeadline);
        }
      }

      setStatus("error");
    }
  };

  // Shared helper: store screening context in sessionStorage before any sign-up navigation
  const storeScreeningContext = (sessionId: string) => {
    if (submittedJobDescriptionRef.current) {
      sessionStorage.setItem("screening_job_description", submittedJobDescriptionRef.current);
    }
  };

  // CTA handler factory: stores job description + navigates to sign-up with appropriate cta_type
  const makeSignUpHandler = (ctaType: string) => () => {
    const sessionId = effectiveResult?.session_id;
    if (ctaType === "try_another") {
      sessionStorage.removeItem("pending_screening_session_id");
      sessionStorage.removeItem("screening_case_type");
      sessionStorage.removeItem("screening_cta_type");
      sessionStorage.removeItem("screening_job_description");
      sessionStorage.removeItem("screening_resume_file_name");
      setPrefillJobDescription(submittedJobDescriptionRef.current);
      setStatus("idle");

      return;
    }
    if (!sessionId) return;
    storeScreeningContext(sessionId);
    navigate(
      `/sign-up?screening_session_id=${encodeURIComponent(sessionId)}&cta_type=${ctaType}`
    );
  };

  return (
    <section className="profile-screening-page">

      {isRateLimited && !rateLimitModalOpen && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            contextLimitType === 'daily'
              ? `Daily limit reached. Resets in ${formatCountdown(countdownSeconds)}`
              : `Hourly limit reached. Try again in ${formatCountdown(countdownSeconds)}`
          }
        />
      )}

      {(effectiveStatus === "idle" || effectiveStatus === "error") && (
        <ScreeningForm
          onSubmit={handleScreeningSubmit}
          isSubmitting={isProcessing}
          initialJobDescription={prefillJobDescription}
          errorState={error && effectiveStatus === "error" ? error : null}
        />
      )}

      {effectiveStatus === "processing" && <ProcessingScreen />}

      {effectiveStatus === "success" && effectiveResult && (
        <ScreeningReport
          sessionId={effectiveResult.session_id}
          report={effectiveResult.report}
          showWatermark={true}
          showPublicCTAs={true}
          // onSignUpCtaClick={makeSignUpHandler("free")}
          onSaveCtaClick={makeSignUpHandler("save")}
          onValidateCtaClick={makeSignUpHandler("validate")}
          onHandleTryAnotherClick={makeSignUpHandler("try_another")}
        />
      )}

      <RateLimitModal
        open={rateLimitModalOpen}
        retryAfter={retryAfter}
        countdownSeconds={countdownSeconds}
        limitType={contextLimitType}
        onClose={closeRateLimitModal}
        onSubmitEmail={handleRateLimitNotify}
      />
    </section>
  );
};

export default ProfileScreeningPage;
